import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, ScanText, FileType2, Combine, Stamp, Crop, Lock, Minimize2, GitBranch,
  FolderInput, Route, Play, FolderCheck, Cpu, UserX, EyeOff, Monitor,
} from 'lucide-react';
import { BlurFade } from '../components/magicui/blur-fade';
import { Marquee } from '../components/magicui/marquee';
import { ShimmerButton } from '../components/magicui/shimmer-button';
import { AnimatedGridPattern } from '../components/magicui/animated-grid-pattern';
import FlowCard from '../components/FlowCard';
import { pdfFlowsUrl } from '../data/site';

// Product facts mirror the live pdf-flows.com site.
const capabilities = [
  { icon: ScanText, title: 'Recognition', text: 'OCR in 180 languages, no connection required' },
  { icon: FileType2, title: 'Conversion', text: 'PDF to DOCX, XLSX, PPTX, HTML, PNG — and back' },
  { icon: Combine, title: 'Merge & split', text: 'By page, by bookmark, or by a rule you write' },
  { icon: Stamp, title: 'Watermark', text: 'Text or image, with dynamic dates and counters' },
  { icon: Crop, title: 'Page work', text: 'Crop, rotate, extract, reorder, renumber' },
  { icon: Lock, title: 'Encryption', text: 'AES-256, separate user and owner keys' },
  { icon: Minimize2, title: 'Compression', text: 'Smart image re-sampling that respects the page' },
  { icon: GitBranch, title: 'Conditions', text: 'If/else branches that route each file its own way' },
];

const steps = [
  { icon: FolderInput, title: 'Add your files', text: 'One PDF or five thousand — PDF Flows queues them up and handles the batch without supervision.' },
  { icon: Route, title: 'Choose a flow', text: 'Start from a ready-made flow, like scans to searchable PDFs, or build your own once and reuse it forever.' },
  { icon: Play, title: 'Let it run', text: 'Parallel workers process the batch. A failed file steps aside, retries and reports — the rest keeps moving.' },
  { icon: FolderCheck, title: 'Collect results', text: 'Everything lands named, sorted and sealed in the folder you chose. Nothing is uploaded along the way.' },
];

const privacy = [
  { icon: Cpu, title: '100% on-device', text: 'Every page is processed on your own CPU — even OCR.' },
  { icon: UserX, title: 'No account', text: 'Install, open, run. There is nothing to sign into.' },
  { icon: EyeOff, title: 'No content telemetry', text: 'Your documents are never collected or analysed.' },
];

const formats = ['PDF', 'DOCX', 'XLSX', 'PPTX', 'HTML', 'PNG', 'OCR · 180 languages', 'AES-256', 'Batch processing', 'Folder watchers', 'Schedulers', 'Renamers & routers'];

export default function PdfFlows() {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-white pb-20 pt-28 lg:pb-28 lg:pt-36">
        <AnimatedGridPattern
          numSquares={28}
          maxOpacity={0.1}
          duration={3}
          className="-z-10 fill-fusion-teal/40 stroke-ink/[0.06] [mask-image:radial-gradient(700px_circle_at_70%_20%,white,transparent)]"
        />
        <div className="container-x">
          <Link to="/#products" className="inline-flex items-center gap-2 text-sm text-ink-3 transition hover:text-ink">
            <ArrowLeft className="size-4" aria-hidden="true" /> All products
          </Link>
          <div className="mt-10 grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <BlurFade>
                <p className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium text-ink-3 shadow-card">
                  <span className="size-1.5 rounded-full bg-fusion-teal" /> A Market Fusion Innovations product
                </p>
              </BlurFade>
              <BlurFade delay={0.1}>
                <h1 className="mt-6 font-display text-[clamp(2.7rem,6vw,4.6rem)] font-semibold leading-[1.02] tracking-[-0.045em]">
                  PDF Flows: your paperwork, <span className="fusion-text">on autopilot.</span>
                </h1>
              </BlurFade>
              <BlurFade delay={0.2}>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-3">
                  PDF Flows is desktop software for Windows that chains recognition, conversion, watermarking,
                  splitting and encryption into repeatable, one-click workflows. Build a flow once, and every
                  folder of PDFs after that is processed automatically — fully offline.
                </p>
              </BlurFade>
              <BlurFade delay={0.3}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <ShimmerButton as="a" href={pdfFlowsUrl} target="_blank" rel="noopener noreferrer" background="#0A0F1F" shimmerColor="#5EEAD4" className="gap-2 text-sm font-semibold">
                    Visit pdf-flows.com <ArrowUpRight className="size-4" aria-hidden="true" />
                  </ShimmerButton>
                  <span className="inline-flex items-center gap-2 text-sm text-ink-3">
                    <Monitor className="size-4" aria-hidden="true" /> Windows 10 / 11 · 64-bit · Free
                  </span>
                </div>
              </BlurFade>
            </div>
            <BlurFade delay={0.25} direction="up" offset={20} className="mx-auto w-full max-w-md lg:max-w-none">
              <FlowCard />
            </BlurFade>
          </div>
        </div>
      </section>

      <div className="border-y border-line bg-mist py-3">
        <Marquee pauseOnHover reverse className="[--duration:30s] [--gap:2rem]">
          {formats.map((f) => (
            <span key={f} className="whitespace-nowrap rounded-full bg-white px-4 py-1.5 font-mono text-xs text-ink-2 shadow-card">
              {f}
            </span>
          ))}
        </Marquee>
      </div>

      <section className="bg-mist py-24 lg:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <BlurFade inView><p className="eyebrow">Capabilities</p></BlurFade>
            <BlurFade inView delay={0.1}>
              <h2 className="h-section mt-5">Eight core tools, designed to work together.</h2>
            </BlurFade>
            <BlurFade inView delay={0.2}>
              <p className="mt-5 text-lg text-ink-3">
                Each tool works on its own — the power is in the chaining. Recognition hands text to the converter,
                the converter hands pages to the stamp, and the stamp to encryption.
              </p>
            </BlurFade>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {capabilities.map(({ icon: Icon, title, text }, i) => (
              <BlurFade key={title} inView delay={0.05 * i} className="h-full">
                <div className="group h-full rounded-3xl bg-white p-6 shadow-card transition-shadow hover:shadow-lift">
                  <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-mist text-fusion-blue transition-colors group-hover:bg-ink group-hover:text-white">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-3">{text}</p>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <BlurFade inView><p className="eyebrow justify-center">How it works</p></BlurFade>
            <BlurFade inView delay={0.1}>
              <h2 className="h-section mt-5">Set the course once. Then let it run.</h2>
            </BlurFade>
          </div>
          <ol className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <li key={title}>
                <BlurFade inView delay={0.08 * i} className="relative h-full rounded-3xl border border-line p-7">
                  <span className="font-mono text-xs text-ink-3">Step {String(i + 1).padStart(2, '0')}</span>
                  <span className="mt-5 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-fusion-blue to-fusion-teal text-white">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{text}</p>
                </BlurFade>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white pb-24 lg:pb-32">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-[2.25rem] bg-ink px-7 py-14 text-white sm:px-12 lg:px-16 lg:py-20">
            <div aria-hidden="true" className="absolute -right-24 -top-24 size-96 rounded-full bg-fusion-blue/30 blur-3xl" />
            <div className="relative grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
              <div>
                <BlurFade inView><p className="eyebrow !text-white/60">Privacy</p></BlurFade>
                <BlurFade inView delay={0.1}>
                  <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.035em] text-balance sm:text-5xl">
                    Nothing you feed it ever leaves your machine.
                  </h2>
                </BlurFade>
                <BlurFade inView delay={0.2}>
                  <p className="mt-5 max-w-md text-white/70">
                    PDF Flows is free for Windows 10 and 11. No subscription, no account, no upload — just your
                    PDFs, finally moving on their own.
                  </p>
                </BlurFade>
                <BlurFade inView delay={0.3}>
                  <ShimmerButton as="a" href={pdfFlowsUrl} target="_blank" rel="noopener noreferrer" background="#3355FF" className="mt-8 inline-flex w-fit gap-2 text-sm font-semibold">
                    Get PDF Flows at pdf-flows.com <ArrowUpRight className="size-4" aria-hidden="true" />
                  </ShimmerButton>
                </BlurFade>
              </div>
              <div className="grid gap-3">
                {privacy.map(({ icon: Icon, title, text }, i) => (
                  <BlurFade key={title} inView delay={0.1 * i}>
                    <div className="flex items-center gap-4 rounded-2xl bg-white/[0.06] p-5 ring-1 ring-white/10">
                      <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/10">
                        <Icon className="size-5" aria-hidden="true" />
                      </span>
                      <div>
                        <p className="font-semibold">{title}</p>
                        <p className="text-sm text-white/65">{text}</p>
                      </div>
                    </div>
                  </BlurFade>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
